import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageSurveyComponent } from '@unity/abstract/abstract-manage-survey.component';
import {
  DisbursementDetailedDto,
  DisbursementsClient,
  DisbursementUpdateDto,
  FormDataDto,
  TransitionMiniDto,
  TransitionRequestDto,
} from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { SurveyDataService } from '@unity/services/survey-data.service';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardManageSurveySharedComponent } from '@unity/shared/composed/card-manage-survey-shared/card-manage-survey-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'unity-manage-disbursement-page',
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_BUTTON,
    AuthorizeDirective,
    PageContainerComponent,
    ActionDeleteSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
    CardManageSurveySharedComponent,
  ],
  templateUrl: './manage-disbursement-page.component.html',
  styleUrl: './manage-disbursement-page.component.scss',
})
export class ManageDisbursementPageComponent extends AbstractManageSurveyComponent<DisbursementDetailedDto> {
  private readonly disbursementsClient = inject(DisbursementsClient);
  private readonly surveyDataService = inject(SurveyDataService);

  /* Inputs from Route */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $disbursementId = input.required({ alias: 'disbursementId', transform: (value: string) => parseInt(value) });

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));

  /* Impl */
  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      const agreementId = this.$agreementId();
      return agreementId && resourceId ? { agreementId, resourceId } : undefined;
    },
    stream: ({ params: { agreementId, resourceId } }) => this.disbursementsClient.getOneForAgreementAsDetailed(agreementId, resourceId),
  });

  protected override readonly $formSchema = computed(() => {
    const resource = this.$resource();
    return resource?.form_schema;
  });

  protected override updateResourceFn$ = (resourceId: number, data: FormDataDto, transition?: TransitionMiniDto) => {
    const agreementId = untracked(this.$agreementId);
    const updateDto: DisbursementUpdateDto = {
      transition_id: transition?.id ?? null,
      ...data,
    };
    return this.disbursementsClient.updateOneForAgreement(agreementId, resourceId, updateDto);
  };

  protected override deleteResourceFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.disbursementsClient.deleteOneForAgreement(agreementId, resourceId);
  };

  protected override editEntitlement: UnityEntitlementPath = 'Disbursement:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Disbursement:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Disbursement:Delete';
  protected override resourceName = 'Disbursement';
  protected override $resourceId = this.$disbursementId;

  protected override availableTransitionsFn$ = (_resourceId: number) => {
    const agreementId = this.$agreementId();
    return this.availableTransitionsClient.getAllForDisbursementAsMini(agreementId, _resourceId);
  };

  protected override doTransitionFn$ = (_resourceId: number, transition: TransitionRequestDto) => {
    const agreementId = this.$agreementId();
    return this.transitionsClient.setForDisbursement(agreementId, _resourceId, transition);
  };

  /* Computed */
  protected readonly $contentHeader = computed(() => {
    const disbursement = this.mainResource.value();
    return `Disbursement: ${disbursement?.reference_number_display}`;
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const disbursement = this.mainResource.value();
    return `Agreement: ${disbursement?.agreement.name}`;
  });

  /* Data */
  public constructor() {
    super();

    effect(() => {
      const agreementId = this.$agreementId();
      this.surveyDataService.setAgreementId(agreementId);
    });
  }
}
